import type { PageEntry, Node, Entry } from 'contensis-delivery-api';
import htmlParser, { HTMLReactParserOptions, domToReact } from 'html-react-parser';
import dynamic from 'next/dynamic';
import { useTheme } from 'styled-components';

import cmsParser from 'Common/util/cmsParser';
import { composerContentCarousel } from 'Common/util/cmsComposer';

import { H3 } from 'Elements/Typography';

import type { Props } from './Residence';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
    residences: Entry[];
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors, residences } = data;

    const {
        title,
        sys,
        //Hero
        heroBannerImageDefault,
        // Sub hero
        priceFrom,
        distanceFromCampus,
        virtualTourLink,
        mapLocation,
        // Features
        residenceFeaturesHeading,
        residenceFeatures,
        // About
        aboutResidenceHeading,
        aboutResidenceDescription,
        // Gallery
        gallery,
        // What's included
        whatsIncludedHeading,
        whatsIncludedList,
        // Info bar,
        infoBar,
        // Facilities
        facilitiesHeading,
        facilitiesDescription,
        // Fees
        feesHeading,
        feesLeftColumn,
        feeRightColumn,
        feesDisclaimer,
        // Map
        mapHeading,
        mapDescription,
        // Promo cards
        gridPromoCard,
        // Related pages
        relatedPagesHeading,
        buttonCTA
    } = entry;

    const { modules } = useTheme();

    const feesParserOptions: HTMLReactParserOptions = {
        replace: domNode => {
            if (domNode.children) {
                switch (domNode.name) {
                    case 'h3':
                        return (
                            <H3
                                size={{ base: '1.25rem', large: '1.5rem' }}
                                spacing={{ base: { margin: 0 } }}
                            >
                                {domNode.children[0].data}
                            </H3>
                        );
                    case 'ul':
                        return (
                            <ul style={{ paddingLeft: '1.25rem' }}>
                                {domToReact(domNode.children)}
                            </ul>
                        );
                    default:
                        return domNode;
                }
            } else {
                return domNode;
            }
        }
    };

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: {
            ...cmsParser.heroBannerWave(heroBannerImageDefault.heroBannerWaveDefault),
            image: cmsParser.image(heroBannerImageDefault.image)
        },
        subHero: {
            price: priceFrom,
            distance: distanceFromCampus,
            tour: virtualTourLink
                ? {
                      label: 'View 360 tour',
                      ariaLabel: 'View 360 tour',
                      href: virtualTourLink.path || virtualTourLink.url,
                      openInNewTab: true
                  }
                : undefined,
            viewOnMap: mapLocation
                ? {
                      label: 'View on map',
                      ariaLabel: 'View on map',
                      href: `https://maps.google.com/?q=${mapLocation.lat},${mapLocation.lon}`,
                      openInNewTab: true
                  }
                : undefined
        },
        features: {
            heading: residenceFeaturesHeading,
            items: {
                items: residenceFeatures.map((item, index) => ({
                    id: item.iconId || index,
                    text: item.listItemText,
                    icon: dynamic(() =>
                        import('@solent-university/solent-icons').then(
                            icons => icons[item.icon.name]
                        )
                    )
                }))
            }
        },
        about: {
            heading: aboutResidenceHeading,
            text: aboutResidenceDescription ? htmlParser(aboutResidenceDescription) : undefined
        },
        gallery: {
            id: 'gallery',
            slides: composerContentCarousel.createSlides(gallery),
            slidesConfig: {
                large: {
                    slidesToShow: 1,
                    gap: 0
                },
                medium: {
                    slidesToShow: 1,
                    gap: 0
                },
                small: {
                    slidesToShow: 1,
                    gap: 0
                }
            }
        },
        included: {
            heading: whatsIncludedHeading,
            items: {
                items: whatsIncludedList.map((item, index) => ({
                    id: item.iconId || index,
                    text: item.listItemText,
                    icon: dynamic(() =>
                        import('@solent-university/solent-icons').then(
                            icons => icons[item.icon.name]
                        )
                    )
                }))
            }
        },
        infoBar: !infoBar.isHidden
            ? {
                  heading: infoBar.heading,
                  text: htmlParser(infoBar.description),
                  icon: dynamic(() =>
                      import('@solent-university/solent-icons').then(
                          icons => icons[infoBar.iconName.name]
                      )
                  ),
                  link:
                      infoBar.link && infoBar.link.link
                          ? cmsParser.arrowLink(infoBar.link)
                          : undefined,
                  backgroundColour: cmsParser.getThemeColour(infoBar.backgroundColour.colourPicker),
                  textColour: cmsParser.getThemeColour(infoBar.textColour)
              }
            : undefined,
        facilities: {
            heading: facilitiesHeading,
            text: facilitiesDescription ? htmlParser(facilitiesDescription) : undefined
        },
        fees: {
            heading: feesHeading,
            leftCol: feesLeftColumn ? htmlParser(feesLeftColumn, feesParserOptions) : undefined,
            rightCol: feeRightColumn ? htmlParser(feeRightColumn, feesParserOptions) : undefined,
            disclaimer: feesDisclaimer
        },
        accommodationMap: {
            heading: mapHeading || 'Solent and our city',
            description:
                mapDescription ||
                'All of Solent’s student residences are in Southampton city centre, close to amenities, shops and entertainment; and none are more than a 20-minute walk from the main campus.',
            viewAllLink: {
                href: '/accommodation/find-a-home',
                label: 'View all accommodation',
                ariaLabel: 'View all accommodation options'
            },
            listings: residences.map((residence, index) => ({
                name: residence.title,
                residenceLink: {
                    id: residence.sys.id,
                    href: residence.sys.uri
                },
                address: residence.heroBannerImageDefault.heroBannerWaveDefault.postHeading,
                excerpt: residence.introText,
                price: residence.priceFrom,
                distance: residence.distanceFromCampus,
                thumbnail: {
                    default: cmsParser.image(residence.listingImage),
                    mobile: cmsParser.image(residence.listingImageLandscape)
                },
                colour: modules.accommodationMap.colours[
                    index % modules.accommodationMap.colours.length
                ],
                mapPosition: {
                    left: residence.mapXcoordinate,
                    bottom: residence.mapYcoordinate
                }
            }))
        },
        promoCards: {
            cards: gridPromoCard.map(card => ({
                id: card.sys.id,
                link: cmsParser.link(card.link)
            }))
        },
        relatedPages: {
            heading: relatedPagesHeading,
            buttons: buttonCTA.map(button => cmsParser.linkButton(button))
        }
    };
};

export default datamap;
